import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import { captureWebError } from "~/sentry";
import { selectSidebarThreadSummaryByRef, selectThreadByRef, useStore } from "~/store";
import {
  deriveThreadDetailLivenessAction,
  THREAD_DETAIL_MAX_RESUBSCRIBE_ATTEMPTS,
  THREAD_DETAIL_MAX_TOTAL_ATTEMPTS,
  THREAD_DETAIL_STALL_THRESHOLD_MS,
  THREAD_DETAIL_WATCHDOG_INTERVAL_MS,
} from "~/lib/threadDetailLiveness";
import { recordThreadSubscriptionFailure } from "./threadSubscriptionFailure";
import {
  threadDetailSubscriptions,
  type ThreadDetailSubscriptionEntry,
} from "./threadDetailSubscriptionState";
import { readThreadDetailSubscriptionDependencies } from "./threadDetailSubscriptionDependencies";

// Thread detail subscription cache policy:
// - Active consumers keep a subscription retained via refCount.
// - Released subscriptions stay warm for a longer idle TTL to avoid churn
//   while moving around the UI.
// - Threads with active work or pending user action are sticky and are never
//   evicted while they remain non-idle.
// - Capacity eviction only targets idle cached subscriptions.
export const THREAD_DETAIL_SUBSCRIPTION_IDLE_EVICTION_MS = 15 * 60 * 1000;
export const MAX_CACHED_THREAD_DETAIL_SUBSCRIPTIONS = 32;
export const NOOP = () => undefined;

export function getThreadDetailSubscriptionKey(
  environmentId: EnvironmentId,
  threadId: ThreadId,
): string {
  return scopedThreadKey(scopeThreadRef(environmentId, threadId));
}

export function clearThreadDetailSubscriptionEviction(
  entry: ThreadDetailSubscriptionEntry,
): ThreadDetailSubscriptionEntry {
  if (entry.evictionTimeoutId !== null) {
    clearTimeout(entry.evictionTimeoutId);
    entry.evictionTimeoutId = null;
  }
  return entry;
}

function isNonIdleThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): boolean {
  const threadRef = scopeThreadRef(entry.environmentId, entry.threadId);
  const state = useStore.getState();
  const sidebarThread = selectSidebarThreadSummaryByRef(state, threadRef);

  // Prefer shell/sidebar state first because it carries the coarse thread
  // readiness flags used throughout the UI (pending approvals/input/plan).
  if (sidebarThread && isNonIdleSidebarThread(sidebarThread)) return true;

  const thread = selectThreadByRef(state, threadRef);
  if (!thread) {
    return false;
  }

  return (
    isActiveOrchestrationStatus(thread.session?.orchestrationStatus) ||
    thread.latestTurn?.state === "running" ||
    thread.pendingSourceProposedPlan !== undefined
  );
}

function isActiveOrchestrationStatus(status: string | undefined): boolean {
  return Boolean(status && status !== "idle" && status !== "stopped");
}

function isNonIdleSidebarThread(
  sidebarThread: NonNullable<ReturnType<typeof selectSidebarThreadSummaryByRef>>,
): boolean {
  return (
    sidebarThread.hasPendingApprovals ||
    sidebarThread.hasPendingUserInput ||
    sidebarThread.hasActionableProposedPlan ||
    isActiveOrchestrationStatus(sidebarThread.session?.orchestrationStatus) ||
    sidebarThread.latestTurn?.state === "running"
  );
}

export function shouldEvictThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): boolean {
  return entry.refCount === 0 && !isNonIdleThreadDetailSubscription(entry);
}

export function attachThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): boolean {
  const connection = readThreadDetailSubscriptionDependencies().readConnection(entry.environmentId);
  // Keep listening while there is nothing to attach to: a connection removed and registered
  // again emits twice, and the first emit still finds no connection.
  if (entry.unsubscribe === NOOP && !connection) {
    return false;
  }
  if (entry.unsubscribeConnectionListener !== null) {
    entry.unsubscribeConnectionListener();
    entry.unsubscribeConnectionListener = null;
  }
  if (entry.unsubscribe !== NOOP || !connection) {
    return true;
  }

  entry.unsubscribe = connection.client.orchestration.subscribeThread(
    { threadId: entry.threadId },
    (item) => {
      // Any item proves the stream is live; reset the liveness watchdog. The
      // opt-in "synchronized" marker is one this app never asks for.
      entry.lastEventAt = Date.now();
      entry.recoveryAttempts = 0;
      if (item.kind === "event") {
        readThreadDetailSubscriptionDependencies().applyEvent(item.event, entry.environmentId);
      } else if (item.kind === "snapshot") {
        useStore.getState().syncServerThreadDetail(item.snapshot.thread, entry.environmentId);
        useStore.getState().clearThreadSubscriptionError(entry.environmentId, entry.threadId);
      }
    },
    {
      onError: (error) =>
        recordThreadSubscriptionFailure(entry.environmentId, entry.threadId, error),
    },
  );
  return true;
}

export function watchThreadDetailSubscriptionConnection(
  entry: ThreadDetailSubscriptionEntry,
): void {
  if (entry.unsubscribeConnectionListener !== null) {
    return;
  }

  entry.unsubscribeConnectionListener =
    readThreadDetailSubscriptionDependencies().subscribeConnections(() => {
      if (attachThreadDetailSubscription(entry)) {
        entry.lastAccessedAt = Date.now();
      }
    });
  attachThreadDetailSubscription(entry);
}

// --- Thread-detail liveness watchdog (F1: stale "Working…" spinner) ------------
// A single timer scans every active thread-detail subscription. When the UI
// believes a turn is running but the stream has been silent past the stall
// threshold, the subscription is forced back to authoritative state: a cheap
// resubscribe first (re-opens the thread stream, which replays a fresh
// snapshot), escalating to a full session reconnect if resubscribes do not
// revive it. Every recovery is reported to Sentry so the exact disconnect class
// can be pinned for a precise follow-up fix.

let threadDetailLivenessWatchdogTimer: ReturnType<typeof setInterval> | null = null;

function isThreadDetailTurnRunning(entry: ThreadDetailSubscriptionEntry): boolean {
  const threadRef = scopeThreadRef(entry.environmentId, entry.threadId);
  const state = useStore.getState();
  const sidebarThread = selectSidebarThreadSummaryByRef(state, threadRef);
  if (
    sidebarThread?.latestTurn?.state === "running" ||
    sidebarThread?.session?.orchestrationStatus === "running"
  ) {
    return true;
  }
  const thread = selectThreadByRef(state, threadRef);
  return (
    thread?.latestTurn?.state === "running" || thread?.session?.orchestrationStatus === "running"
  );
}

function forceReattachThreadDetailSubscription(entry: ThreadDetailSubscriptionEntry): void {
  if (entry.unsubscribe !== NOOP) {
    entry.unsubscribe();
    entry.unsubscribe = NOOP;
  }
  if (!attachThreadDetailSubscription(entry)) {
    // The connection is not ready yet; ensure the connection listener is
    // installed so it re-attaches the moment a connection registers.
    watchThreadDetailSubscriptionConnection(entry);
  }
}

function recoverStalledThreadDetailSubscription(
  entry: ThreadDetailSubscriptionEntry,
  action: "resubscribe" | "reconnect",
  msSinceLastEvent: number,
): void {
  entry.recoveryAttempts += 1;
  // Treat the recovery itself as activity so the next scan waits a full
  // threshold for the resubscribe/reconnect to deliver a snapshot before
  // escalating again.
  entry.lastEventAt = Date.now();

  // Console signal on EVERY recovery action so the watchdog firing is directly
  // observable in DevTools (and via tooling that reads console logs) without
  // needing Sentry access. Sentry stays reconnect-only (below) to avoid
  // alarm noise; the console line is a cheap, non-alarming diagnostic.
  console.warn("[forge] thread-detail liveness watchdog recovering", {
    action,
    threadId: String(entry.threadId),
    environmentId: String(entry.environmentId),
    msSinceLastEvent: Math.round(msSinceLastEvent),
    recoveryAttempts: entry.recoveryAttempts,
  });

  if (action === "resubscribe") {
    // Routine self-heal: a brief socket drop or a legitimately quiet long tool
    // call. A delivered snapshot resets the attempt counter, so this stays
    // out of Sentry to avoid alarm noise on expected stream gaps.
    forceReattachThreadDetailSubscription(entry);
    return;
  }

  // Escalation: resubscribes delivered nothing, so the socket is likely dead.
  // Reconnect is the intended self-heal path; keep it as a structured console
  // diagnostic and reserve Sentry for a failed reconnect operation.
  void readThreadDetailSubscriptionDependencies()
    .readConnection(entry.environmentId)
    ?.reconnect()
    .catch((error: unknown) => {
      captureWebError(error, {
        route: "thread-detail-liveness-watchdog-reconnect-failed",
        threadId: String(entry.threadId),
        environmentId: String(entry.environmentId),
        msSinceLastEvent: String(Math.round(msSinceLastEvent)),
        recoveryAttempts: String(entry.recoveryAttempts),
      });
    });
}

function runThreadDetailLivenessScan(): void {
  const now = Date.now();
  for (const entry of threadDetailSubscriptions.values()) {
    const action = deriveThreadDetailLivenessAction({
      isWorking: isThreadDetailTurnRunning(entry),
      msSinceLastEvent: now - entry.lastEventAt,
      stallThresholdMs: THREAD_DETAIL_STALL_THRESHOLD_MS,
      recoveryAttempts: entry.recoveryAttempts,
      maxResubscribeAttempts: THREAD_DETAIL_MAX_RESUBSCRIBE_ATTEMPTS,
      maxTotalAttempts: THREAD_DETAIL_MAX_TOTAL_ATTEMPTS,
    });
    if (action === "none") {
      continue;
    }
    recoverStalledThreadDetailSubscription(entry, action, now - entry.lastEventAt);
  }
}

export function startThreadDetailLivenessWatchdog(): void {
  if (threadDetailLivenessWatchdogTimer !== null) {
    return;
  }
  threadDetailLivenessWatchdogTimer = setInterval(
    runThreadDetailLivenessScan,
    THREAD_DETAIL_WATCHDOG_INTERVAL_MS,
  );
}

function stopThreadDetailLivenessWatchdogIfIdle(): void {
  if (threadDetailSubscriptions.size > 0) {
    return;
  }
  if (threadDetailLivenessWatchdogTimer !== null) {
    clearInterval(threadDetailLivenessWatchdogTimer);
    threadDetailLivenessWatchdogTimer = null;
  }
}

export function disposeThreadDetailSubscriptionByKey(key: string): boolean {
  const entry = threadDetailSubscriptions.get(key);
  if (!entry) {
    return false;
  }

  clearThreadDetailSubscriptionEviction(entry);
  entry.unsubscribeConnectionListener?.();
  entry.unsubscribeConnectionListener = null;
  threadDetailSubscriptions.delete(key);
  entry.unsubscribe();
  entry.unsubscribe = NOOP;
  stopThreadDetailLivenessWatchdogIfIdle();
  return true;
}
